"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.YearnVaultMarketCapService = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const types_1 = require("@shapeshiftoss/types");
const head_1 = __importDefault(require("lodash/head"));
const config_1 = require("../config");
const bignumber_1 = require("../utils/bignumber");
const isValidDate_1 = require("../utils/isValidDate");
const rateLimiters_1 = require("../utils/rateLimiters");
const gql_queries_1 = require("./gql-queries");
const rateLimiter = (0, rateLimiters_1.createRateLimiter)(config_1.RATE_LIMIT_THRESHOLDS_PER_MINUTE.DEFAULT);
const USDC_PRECISION = 6;
class YearnVaultMarketCapService {
    constructor(args) {
        this.baseUrl = 'https://api.yearn.finance';
        this.defaultGetByMarketCapArgs = {
            count: 2500,
        };
        this.yearnSdk = args.yearnSdk;
    }
    async findAll(args) {
        try {
            const argsToUse = { ...this.defaultGetByMarketCapArgs, ...args };
            const response = await rateLimiter(() => this.yearnSdk.vaults.get());
            const vaults = response.slice(0, argsToUse.count);
            return vaults
                .sort((a, b) => (0, bignumber_1.bnOrZero)(a.underlyingTokenBalance.amountUsdc).lt(b.underlyingTokenBalance.amountUsdc)
                ? 1
                : -1)
                .reduce((acc, yearnItem) => {
                const assetId = (0, caip_1.toAssetId)({
                    chainNamespace: caip_1.CHAIN_NAMESPACE.Evm,
                    chainReference: caip_1.CHAIN_REFERENCE.EthereumMainnet,
                    assetNamespace: 'erc20',
                    assetReference: yearnItem.address,
                });
                // if amountUsdc of a yearn asset is 0, the asset has not price or value
                if ((0, bignumber_1.bnOrZero)(yearnItem.underlyingTokenBalance.amountUsdc).eq(0)) {
                    acc[assetId] = {
                        price: '0',
                        marketCap: '0',
                        volume: '0',
                        changePercent24Hr: 0,
                    };
                    return acc;
                }
                let volume = (0, bignumber_1.bn)('0');
                let changePercent24Hr = 0;
                const price = (0, bignumber_1.bnOrZero)(yearnItem.underlyingTokenBalance.amountUsdc)
                    .div('1e+6')
                    .div(yearnItem.underlyingTokenBalance.amount)
                    .times(`1e+${yearnItem.decimals}`)
                    .times(yearnItem.metadata.pricePerShare)
                    .div(`1e+${yearnItem.decimals}`)
                    .toString();
                const marketCap = (0, bignumber_1.bnOrZero)(yearnItem.underlyingTokenBalance.amountUsdc)
                    .div('1e+6')
                    .toFixed(2);
                const historicEarnings = yearnItem.metadata.historicEarnings;
                const lastHistoricalEarnings = historicEarnings
                    ? historicEarnings[historicEarnings.length - 1]
                    : null;
                const secondToLastHistoricalEarnings = historicEarnings
                    ? historicEarnings[historicEarnings.length - 2]
                    : null;
                if (lastHistoricalEarnings && secondToLastHistoricalEarnings) {
                    volume = (0, bignumber_1.bnOrZero)(lastHistoricalEarnings.earnings.amountUsdc).minus(secondToLastHistoricalEarnings.earnings.amountUsdc);
                }
                if (lastHistoricalEarnings) {
                    changePercent24Hr =
                        volume
                            .div(lastHistoricalEarnings.earnings.amountUsdc)
                            .div(`1e+${USDC_PRECISION}`)
                            .toNumber() || 0;
                }
                acc[assetId] = {
                    price,
                    marketCap,
                    volume: volume.abs().toString(),
                    changePercent24Hr,
                };
                return acc;
            }, {});
        }
        catch (e) {
            console.info(e);
            return {};
        }
    }
    async findByAssetId({ assetId }) {
        const id = caip_1.adapters.assetIdToYearn(assetId);
        if (!id)
            return null;
        try {
            const vaults = await rateLimiter(() => this.yearnSdk.vaults.get([id]));
            if (!vaults || !vaults.length)
                return null;
            const vault = (0, head_1.default)(vaults);
            if (!vault)
                return null;
            if ((0, bignumber_1.bnOrZero)(vault.underlyingTokenBalance.amountUsdc).eq(0)) {
                return {
                    price: '0',
                    marketCap: '0',
                    volume: '0',
                    changePercent24Hr: 0,
                };
            }
            let volume = (0, bignumber_1.bn)('0');
            let changePercent24Hr = 0;
            const price = (0, bignumber_1.bnOrZero)(vault.underlyingTokenBalance.amountUsdc)
                .div('1e+6')
                .div(vault.underlyingTokenBalance.amount)
                .times(`1e+${vault.decimals}`)
                .times(vault.metadata.pricePerShare)
                .div(`1e+${vault.decimals}`)
                .toString();
            const marketCap = (0, bignumber_1.bnOrZero)(vault.underlyingTokenBalance.amountUsdc).div('1e+6').toFixed(2);
            const historicEarnings = vault.metadata.historicEarnings;
            const lastHistoricalEarnings = historicEarnings
                ? historicEarnings[historicEarnings.length - 1]
                : null;
            const secondToLastHistoricalEarnings = historicEarnings
                ? historicEarnings[historicEarnings.length - 2]
                : null;
            if (lastHistoricalEarnings && secondToLastHistoricalEarnings) {
                volume = (0, bignumber_1.bnOrZero)(lastHistoricalEarnings.earnings.amountUsdc)
                    .minus(secondToLastHistoricalEarnings.earnings.amountUsdc)
                    .div(`1e+${USDC_PRECISION}`)
                    .dp(2);
            }
            if (lastHistoricalEarnings) {
                changePercent24Hr =
                    volume
                        .div(lastHistoricalEarnings.earnings.amountUsdc)
                        .times(`1e+${USDC_PRECISION}`)
                        .toNumber() || 0;
            }
            return {
                price,
                marketCap,
                volume: volume.abs().toString(),
                changePercent24Hr,
            };
        }
        catch (e) {
            console.warn(e);
            throw new Error('YearnMarketService(findByAssetId): error fetching market data');
        }
    }
    getDate(daysAgo) {
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        return date;
    }
    async findPriceHistoryByAssetId({ assetId, timeframe, }) {
        const id = caip_1.adapters.assetIdToYearn(assetId);
        if (!id)
            return [];
        try {
            let daysAgo;
            switch (timeframe) {
                case types_1.HistoryTimeframe.HOUR:
                    daysAgo = 2;
                    break;
                case types_1.HistoryTimeframe.DAY:
                    daysAgo = 3;
                    break;
                case types_1.HistoryTimeframe.WEEK:
                    daysAgo = 7;
                    break;
                case types_1.HistoryTimeframe.MONTH:
                    daysAgo = 30;
                    break;
                case types_1.HistoryTimeframe.YEAR:
                    daysAgo = 365;
                    break;
                case types_1.HistoryTimeframe.ALL:
                    daysAgo = 3650;
                    break;
                default:
                    daysAgo = 1;
            }
            const vaults = await rateLimiter(() => this.yearnSdk.vaults.get([id]));
            if (!vaults || !vaults.length)
                return [];
            const decimals = vaults[0].decimals;
            const params = {
                id,
                shareToken: id,
                fromDate: this.getDate(daysAgo).getTime().toString(),
                toDate: this.getDate(0).getTime().toString(),
            };
            const response = (await rateLimiter(() => this.yearnSdk.services.subgraph.fetchQuery(gql_queries_1.ACCOUNT_HISTORIC_EARNINGS, params)));
            const vaultDayData = response.data.account.vaultPositions[0].vault.vaultDayData;
            return vaultDayData.reduce((acc, current) => {
                const date = Number(current.timestamp);
                if (!(0, isValidDate_1.isValidDate)(date)) {
                    console.error('Yearn SDK vault has invalid date');
                    return acc;
                }
                const price = (0, bignumber_1.bn)(current.tokenPriceUSDC)
                    .div(`1e+${USDC_PRECISION}`)
                    .times(current.pricePerShare)
                    .div(`1e+${decimals}`)
                    .dp(6);
                if (price.isNaN()) {
                    console.error('Yearn SDK vault has invalid price');
                    return acc;
                }
                acc.push({
                    date,
                    price: price.toNumber(),
                });
                return acc;
            }, []);
        }
        catch (e) {
            console.warn(e);
            throw new Error('YearnMarketService(getPriceHistory): error fetching price history');
        }
    }
}
exports.YearnVaultMarketCapService = YearnVaultMarketCapService;
